import { SEARCH_FLAGS } from '@/constants/experimentConstants';
import useViewToggle from '@/features/searchV2/components/ViewToggle/ViewToggle';
import { SEARCH_RESULT_PER_PAGE_LIMIT } from '@/features/searchV2/constants';
import { getSearchPerformedTrackingProps, trackSearchPerformed } from '@/features/searchV2/helpers/tracking';
import { SearchContext as TSearchContext } from '@/features/searchV2/types/tracking';
import { safeEncodeURIComponent } from '@/helpers/urlTs';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import { SEARCH_ACTION, SearchState } from '@/reducers/searchV2Reducer';
import { placeService } from '@/services';
import { searchFeatureFlags as searchFeatureFlagsType, UrlSearchState } from '@/types/state/search';
import debounce from 'lodash.debounce';
import { createContext, useContext, useEffect, useRef } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '..';
import {
  DEFAULT_MY_LOCATION_KEYWORD,
  DEFAULT_SEARCH_KEYWORD,
  DEFAULT_SEARCH_LOCATION,
  SEARCH_PAGE_VIEWS,
} from './useSearch.constants';
import {
  buildServerRequestSearchParams,
  buildUrlSearchParams,
  getSearchUrlParameters,
  requestAndSetExtendedSearchResults,
  requestAndSetSearchResults,
  SearchType,
  shouldSyncClientSearchQuery,
  trackSearchResultsCustomerIO,
} from './useSearch.helpers';
type UseSearchManagerResult = ReturnType<typeof useSearchManager>;

export type SearchManagerOutput = ReturnType<typeof useSearchManager>;

export type SearchManagerProps = {
  type: SearchType;
};

const DEBOUNCE_SEARCH_REQUEST_MS = 500;

/**
 * Change the URL to sync url with search state
 */
function changeUrl(
  { type, what, where, searchQuery }: { type: SearchType; what: string; where: string; searchQuery?: string },
  history,
  location,
) {
  // This urls should be decoded here
  // fix only %
  what = (what || '').trim() || DEFAULT_SEARCH_KEYWORD;
  where = (where || '').trim() || DEFAULT_SEARCH_LOCATION;
  what = what.split('%25').join('%').split('%').join('%25');
  where = where.split('%25').join('%').split('%').join('%25');

  const pathname = `${type === 'sistaminuten' ? '/sistaminuten' : ''}/${safeEncodeURIComponent(
    what,
  )}/${safeEncodeURIComponent(where)}`;

  history.push({ pathname, search: searchQuery, state: { ...location?.state } });
}

const useSearchManager = ({ type }: SearchManagerProps) => {
  const searchState = useAppSelector((state) => state.searchV2);
  const dispatcher = useAppDispatch();
  const match = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const urlState = getSearchUrlParameters({ match });
  const viewToggle = useViewToggle({
    views: [...SEARCH_PAGE_VIEWS],
    initialState: SEARCH_PAGE_VIEWS[0],
    className: 'px-sm',
  });
  const flags = useAppSelector((state) => state.flags);
  const getVariant = useGetAmplitudeExperimentVariant();

  // search flags
  let searchFlagsToSend: searchFeatureFlagsType = {};
  SEARCH_FLAGS.map((flag) => {
    let value = undefined;
    if ((value = flags[flag]?.value ?? getVariant(flag)?.value ?? undefined)) {
      searchFlagsToSend[flag] = value.toString();
    }
  });

  const handleChangeUrl = (what = DEFAULT_SEARCH_KEYWORD, where = DEFAULT_SEARCH_LOCATION, searchQuery: string) =>
    changeUrl({ type, what, where, searchQuery }, history, location);

  /**
   * Perform search on mount if not the search results are already fetched
   * on SSR
   */
  useEffect(() => {
    if (!searchState.isSSRFetched) {
      performSearch(urlState, { searchContext: location?.state?.searchContext ?? 'navigation' });
    } else {
      // if search results are already fetched, send a page view event
      trackSearchResultsCustomerIO(urlState, searchState);

      trackSearchPerformed(
        getSearchPerformedTrackingProps({
          urlState,
          searchState,
          context: 'navigation',
          screenName: type === 'sistaminuten' ? 'deals' : 'search_results',
        }),
      );
    }
    return () => {
      dispatcher({ type: SEARCH_ACTION.SET_RESET_RESULTS });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchPerformed = () => {};

  /**
   * wait for all changes to be done before triggering search to avoid multiple requests
   */
  const debouncedSearch = useRef(
    debounce(
      (
        params: UrlSearchState,
        state: SearchState,
        resetCache: boolean,
        searchContext: TSearchContext,
        bounds: string,
      ) => {
        requestAndSetSearchResults(
          {
            type,
            view: viewToggle.view,
            params,
            state,
            bounds,
            searchContext,
            resetAllSearchItems: resetCache,
          },
          dispatcher,
          handleSearchPerformed,
        );
      },
      DEBOUNCE_SEARCH_REQUEST_MS,
    ),
  ).current;

  /**
   * Navigation initiated Search request (forces a new search)
   * and skips updating the url state (only used for navigation and not user initiated search)
   */
  function performSyncedSearch() {
    const searchParams = buildServerRequestSearchParams(urlState, urlState, searchState);
    debouncedSearch(
      { ...searchParams, q: urlState.q, location: urlState.location },
      { ...searchState, searchContext: location?.state?.searchContext ?? searchState.searchContext },
      true,
    );
  }

  function performSearch(
    params: UrlSearchState,
    options: {
      extendedSearch?: boolean;
      bounds?: string;
      searchContext?: TSearchContext;
    } = {
      extendedSearch: false,
      bounds: undefined,
      searchContext: undefined,
    },
    onCachedResults?: () => void,
  ) {
    const q = (() => {
      if ('q' in params) {
        return params.q;
      }
      return urlState.q;
    })();

    const location = (() => {
      if ('location' in params) {
        return params.location;
      }
      return urlState.location;
    })();

    const page = (() => {
      if ('page' in params) {
        return params.page;
      }

      return urlState.page;
    })();

    const sort = (() => {
      if ('sort' in params) {
        return params.sort;
      }
      return urlState.sort;
    })();

    const explain = (() => {
      if ('explain' in params) {
        return params.explain;
      }
      return urlState.explain;
    })();

    const updatedKeyword = q !== urlState.q;
    const updatedLocation = location !== urlState.location;
    const updatedPage = page !== urlState.page;
    const updatedSearchQuery = shouldSyncClientSearchQuery(params, urlState);

    /**
     * If keyword, location or search query is updated, but page param is not
     * then we need to reset the page to 0 and perform a new search (i.e not use cached results)
     */
    const resetSearch = (updatedKeyword || updatedLocation || updatedSearchQuery) && !updatedPage;

    if (resetSearch) {
      params['page'] = null;
    }

    /**
     * If location is updated and is not set to currentLocation or sorting preference is set
     * to "closest", remove the lat and lon params
     */
    if (updatedLocation && location !== DEFAULT_MY_LOCATION_KEYWORD && sort !== 'closest') {
      params['lat'] = null;
      params['lon'] = null;
    }

    if (explain) {
      params['explain'] = explain || undefined;
    }

    if (Object.keys(searchFlagsToSend).length) {
      params['searchFeatureFlags'] = searchFlagsToSend;
    }

    /**
     * Check if search contains any search params, and if any new search params are added that needs sync
     * if key is included in params then it should override the urlState
     */
    const searchParams = buildServerRequestSearchParams(params, urlState, searchState);
    const urlSearchParams = buildUrlSearchParams(params, urlState);

    if (updatedKeyword || updatedLocation || updatedSearchQuery || resetSearch) {
      const searchQuery = urlSearchParams.toString() ?? window.location.search.replace('?', '');
      handleChangeUrl(q, location, searchQuery);
    }

    if (options.extendedSearch) {
      requestAndSetExtendedSearchResults({ type, params: urlState, state: searchState }, dispatcher);
      return;
    }

    if (options.bounds) {
      requestAndSetSearchResults(
        {
          type,
          view: viewToggle.view,
          params: urlState,
          state: searchState,
          resetAllSearchItems: resetSearch,
          bounds: options.bounds,
          searchContext: options.searchContext,
        },
        dispatcher,
        handleSearchPerformed,
      );
      return;
    }

    const tryGetCachedResults = !resetSearch;

    if (tryGetCachedResults) {
      const currentPage = Math.max(0, Number(urlState.page) || 0);
      /**
       * if params.page is set, use that, otherwise use the current page
       * since the page is not being changed
       */
      const newPage = (() => {
        if ('page' in params) {
          return params.page || 0;
        } else {
          return currentPage;
        }
      })();

      const cachedResults = searchState.places?.[newPage] || [];

      if (cachedResults.length) {
        onCachedResults?.();
        return;
      }
    }

    placeService.removeStoredPlaceImpressions();

    debouncedSearch({ ...searchParams, q, location }, searchState, resetSearch, options.searchContext, options.bounds);
  }

  function paginate({ onCachedResults }: { onCachedResults?: () => void }) {
    const { extendSearchIteration, results } = searchState;
    return {
      nextPage: () => {
        const currentPage = Math.max(0, Number(urlState.page) || 0);
        const extendedSearch = Boolean(extendSearchIteration) && results <= currentPage * SEARCH_RESULT_PER_PAGE_LIMIT;
        performSearch({ page: currentPage + 1 }, { extendedSearch }, onCachedResults);
      },
      prevPage: () => {
        const currentPage = Math.max(0, Number(urlState.page) || 0);
        const page = currentPage > 1 ? currentPage - 1 : null;
        performSearch({ page }, { extendedSearch: Boolean(extendSearchIteration) }, onCachedResults);
      },
    };
  }

  return {
    q: urlState.q?.toLowerCase() !== DEFAULT_SEARCH_KEYWORD ? urlState.q : '',
    location:
      urlState.location?.toLowerCase() !== DEFAULT_SEARCH_LOCATION && urlState.location?.toLowerCase() !== 'var'
        ? urlState.location
        : '',
    startDate: urlState.startDate,
    endDate: urlState.endDate,
    timeOfDay: urlState.timeOfDay,
    prefs: urlState.prefs,
    sort: urlState.sort,
    lat: urlState.lat,
    lon: urlState.lon,
    page: urlState?.page || 0,
    places: searchState?.places || [],
    topSearch: searchState?.topSearch ?? { places: [] },
    results: searchState?.results || 0,
    fetching: searchState?.fetching || false,
    searchContext: searchState.searchContext,
    type,
    performSearch,
    performSyncedSearch,
    paginate,
    viewToggle,
    debugLocation: searchState?.debugLocation || undefined,
    isIndexable: searchState.isIndexable,
  };
};

export const SearchContext = createContext<UseSearchManagerResult>({
  type: 'default',
  q: '',
  location: '',
  startDate: '',
  endDate: '',
  timeOfDay: undefined,
  places: [],
  topSearch: { places: [] },
  results: 0,
  fetching: false,
  page: 0,
  prefs: undefined,
  sort: undefined,
  lat: undefined,
  lon: undefined,
  searchContext: undefined,
  performSearch: () => {},
  performSyncedSearch: () => {},
  paginate: () => ({ nextPage: () => {}, prevPage: () => {} }),
  viewToggle: {
    view: null,
    setView: () => {},
    render: null,
  },
  debugLocation: undefined,
  isIndexable: true,
});

export const useSearchContext = (): UseSearchManagerResult => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearchContext must be used within a SearchProvider');
  }

  return context;
};

export const SearchProvider: React.FC<{
  type: SearchManagerProps['type'];
  children: React.ReactNode;
}> = ({ type, children }) => {
  const manager = useSearchManager({ type });
  return <SearchContext.Provider value={manager}>{children}</SearchContext.Provider>;
};
